/**
 * Sales Insights access for routing and nav. A PC user has access when their
 * Clerk publicMetadata.salesInsights names the active org's profit center.
 * A user whose marker names a different PC is still a Sales Insights user:
 * they land on /pc/insights and are told to switch orgs, rather than being
 * treated as counter staff. The server enforces the same rule
 * (require-sales-insights.ts); this only decides where the user lands.
 */

import { useOrganization, useUser } from "@clerk/clerk-react";
import { getSalesInsightsMetadata } from "@onsemble/shared/types/sales-insights";
import type { OrgMetadata } from "./use-org-metadata";

export const SALES_INSIGHTS_ROUTE = "/pc/insights";

/** Where a user stands with Sales Insights in the active org. */
export type SalesInsightsStatus =
  // No marker (or a malformed one), or the active org isn't a PC
  | { kind: "none" }
  // Marker matches the active PC
  | { kind: "ok" }
  // Marker names a different PC than the one they're signed into
  | { kind: "wrong-pc"; markerPc: string; orgPc: string | null };

/**
 * For callers that already hold the user and org metadata, so the answer
 * can't lag behind their own loading state.
 */
export function getSalesInsightsStatus(
  user: { publicMetadata?: unknown } | null | undefined,
  metadata: OrgMetadata | null,
): SalesInsightsStatus {
  // Not loaded yet, or not in a PC org
  if (!user || metadata?.type !== "profit-center") return { kind: "none" };
  // No usable marker on the user
  const marker = getSalesInsightsMetadata(user);
  if (!marker) return { kind: "none" };
  // Marker names this PC
  const orgPc = metadata.hajocaProfitCenterId ?? null;
  if (orgPc && marker.hajocaProfitCenterId === orgPc) return { kind: "ok" };
  // Marker names some other PC
  return { kind: "wrong-pc", markerPc: marker.hajocaProfitCenterId, orgPc };
}

/**
 * The user is a Sales Insights user here, whether or not the marker matches
 * this PC. Routing and nav use this, so a mismatch lands on /pc/insights.
 */
export function isSalesInsightsUser(
  user: { publicMetadata?: unknown } | null | undefined,
  metadata: OrgMetadata | null,
): boolean {
  return getSalesInsightsStatus(user, metadata).kind !== "none";
}

/**
 * The status, read straight from Clerk rather than through useOrgMetadata,
 * which copies it into state a render later and loops when the org object
 * changes identity every render (as it does in tests).
 */
export function useSalesInsightsStatus(): SalesInsightsStatus {
  const { user } = useUser();
  const { organization } = useOrganization();
  const metadata = (organization?.publicMetadata ?? null) as OrgMetadata | null;
  return getSalesInsightsStatus(user, metadata);
}
